import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { fmtSignalTime, fmtUsd } from '../utils/fmtPrice';
import { formatSideLabel, sideLabelCssClass } from '../utils/tradeActionUi';
import './SignalQueueSection.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

function statusPillClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'filled') return 'pill status-filled';
  if (s === 'pending' || s === 'approved' || s === 'executing') return 'pill status-pending';
  if (s === 'failed' || s === 'rejected') return 'pill status-failed';
  if (s === 'cancelled') return 'pill status-cancelled';
  return 'pill';
}

function signalSortKey(s) {
  const t = Date.parse(s.executedAt || s.createdAt || '');
  return Number.isFinite(t) ? t : 0;
}

function BatchSignalRow({ s }) {
  return (
    <tr>
      <td>{fmtSignalTime(s.executedAt || s.createdAt)}</td>
      <td>{s.symbol}</td>
      <td>
        <span className={sideLabelCssClass(s.side)}>{formatSideLabel(s.side, s.action)}</span>
      </td>
      <td>{String(s.action || 'open').toUpperCase()}</td>
      <td>{fmtUsd(s.sizeUsd)}</td>
      <td>{s.leverage != null ? `${s.leverage}x` : '—'}</td>
      <td>
        <span className={statusPillClass(s.status)}>{s.status}</span>
      </td>
      <td className="signal-reason-cell">{s.reason || '—'}</td>
      <td className="signal-error-cell">{s.errorMessage || '—'}</td>
    </tr>
  );
}

export default function OmniMapActiveBatchSection({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data: batch } = await axios.get(`${API_BASE}/multi-asset/admin/omni/active-batch`, {
        headers: authHeaders(),
      });
      setData(batch);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load, refreshKey]);

  const meta = data?.batchMeta;
  const signals = useMemo(
    () => [...(data?.signals || [])].sort((a, b) => signalSortKey(b) - signalSortKey(a)),
    [data?.signals]
  );
  const outcomes = data?.outcomes || {};

  return (
    <section className="obot-card omni-active-batch-section">
      <h2>Active MAP signal batch</h2>
      <p className="hint">
        Signals from the latest <code>omni-map-active.json</code> apply until the next inbox file
        arrives. Prior pending inbox signals are cancelled when a new batch is applied.
      </p>

      {loading && !data && <p className="hint">Loading batch…</p>}
      {error && <p className="obot-error">{error}</p>}

      {meta && (
        <div className="omni-batch-meta">
          <span>
            Batch: <code>{data.batchId}</code>
          </span>
          {meta.author ? <span>Author: {meta.author}</span> : null}
          {meta.generatedAt ? <span>AI generated: {fmtSignalTime(meta.generatedAt)}</span> : null}
          {meta.appliedAt ? <span>Applied: {fmtSignalTime(meta.appliedAt)}</span> : null}
          {data.globalBotEnabled === false ? (
            <span className="pill status-failed">Global bot OFF — signals will not execute</span>
          ) : (
            <span className="pill status-filled">Global bot ON</span>
          )}
        </div>
      )}

      {!loading && !meta && !error && (
        <p className="hint">No inbox batch applied yet. Drop omni-map-active.json into the MAP inbox.</p>
      )}

      {signals.length > 0 && (
        <>
          <div className="omni-batch-summary">
            <span>{outcomes.pending?.length || 0} pending</span>
            <span>{outcomes.filled?.length || 0} filled</span>
            <span>{outcomes.failed?.length || 0} failed</span>
            <span>{outcomes.rejected?.length || 0} rejected</span>
            <span>{outcomes.cancelled?.length || 0} cancelled</span>
            <span className="omni-batch-sort-note">Newest first · {signals.length} total</span>
          </div>
          <div className="signal-queue-table-wrap omni-batch-table-scroll">
            <table className="signal-queue-table omni-batch-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Symbol</th>
                  <th>Trade</th>
                  <th>Action</th>
                  <th>Margin</th>
                  <th>Lev</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {signals.map((s) => (
                  <BatchSignalRow key={s.signalId} s={s} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
